import React, { useEffect, useState, useCallback } from "react";
import { ListTodo, Loader2, Plus, Check, Trash2, Flag, Calendar } from "lucide-react";
import { api, adminHeaders } from "@/lib/api";
import { toast } from "sonner";

const h = () => ({ headers: adminHeaders() });
const today = () => new Date().toISOString().slice(0, 10);

const PRIO = {
  high: "text-red-300 border-red-500/40 bg-red-500/10",
  normal: "text-gold-soft border-[color:var(--border-strong)] bg-gold/10",
  low: "text-white/60 border-white/15 bg-white/5",
};

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("normal");
  const [due, setDue] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/tasks", h()).then(({ data }) => setTasks(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) { toast.error("Enter a task"); return; }
    setAdding(true);
    try {
      await api.post("/admin/tasks", { title, priority, due_date: due }, h());
      setTitle(""); setDue(""); setPriority("normal");
      toast.success("Task added");
      load();
    } catch { toast.error("Could not add task"); }
    finally { setAdding(false); }
  };

  const toggle = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    setTasks((ts) => ts.map((x) => x.id === t.id ? { ...x, status: next } : x));
    try { await api.patch(`/admin/tasks/${t.id}`, { status: next }, h()); load(); }
    catch { toast.error("Update failed"); load(); }
  };

  const remove = async (id) => {
    setTasks((ts) => ts.filter((x) => x.id !== id));
    try { await api.delete(`/admin/tasks/${id}`, h()); } catch { toast.error("Delete failed"); load(); }
  };

  const open = tasks.filter((t) => t.status !== "done");
  const done = tasks.filter((t) => t.status === "done");

  return (
    <div className="space-y-6" data-testid="admin-tasks">
      <div>
        <p className="kicker mb-1">Command</p>
        <h2 className="font-display text-2xl font-semibold text-white flex items-center gap-2"><ListTodo className="h-5 w-5 text-gold-soft" /> Tasks &amp; reminders</h2>
        <p className="mt-1 text-sm text-muted-c">Everything you need to chase — leads to call, jobs to prep, things to buy.</p>
      </div>

      {/* Add row */}
      <div className="ascendia-card p-4 flex flex-col sm:flex-row gap-2.5" data-testid="task-add-row">
        <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="task-input"
          placeholder="Add a task…" className="flex-1 rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-3.5 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]" />
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2.5 text-sm text-white">
          <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
        </select>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2.5 text-sm text-white" />
        <button onClick={add} disabled={adding} data-testid="task-add-btn" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-4 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-60">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center text-muted-c"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="space-y-2.5">
          {open.length === 0 && done.length === 0 && (
            <div className="ascendia-card p-10 text-center text-muted-c">No tasks yet. Add your first above.</div>
          )}
          {open.map((t) => {
            const overdue = t.due_date && t.due_date < today();
            return (
              <div key={t.id} className="ascendia-card p-3.5 flex items-center gap-3" data-testid={`task-${t.id}`}>
                <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-white/25 hover:border-gold text-transparent hover:text-gold-soft transition-colors"><Check className="h-3.5 w-3.5" /></button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{t.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${PRIO[t.priority] || PRIO.normal}`}><Flag className="h-3 w-3" /> {t.priority}</span>
                    {t.due_date && <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${overdue ? "text-red-300 border-red-500/40 bg-red-500/10" : "text-muted-c border-white/15"}`}><Calendar className="h-3 w-3" /> {overdue ? "Overdue · " : ""}{t.due_date}</span>}
                    {t.linked_label && <span className="text-muted-c">· {t.linked_label}</span>}
                  </div>
                </div>
                <button onClick={() => remove(t.id)} data-testid={`task-delete-${t.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-white/40 hover:text-red-300 hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
          {done.length > 0 && (
            <div className="pt-4">
              <p className="text-xs uppercase tracking-wide text-muted-c mb-2">Completed ({done.length})</p>
              {done.map((t) => (
                <div key={t.id} className="ascendia-card p-3.5 flex items-center gap-3 opacity-60" data-testid={`task-${t.id}`}>
                  <button onClick={() => toggle(t)} className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"><Check className="h-3.5 w-3.5" /></button>
                  <p className="flex-1 text-sm text-white/70 line-through truncate">{t.title}</p>
                  <button onClick={() => remove(t.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-white/40 hover:text-red-300 hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
